import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Flame, TrendingUp, Clock, Dumbbell, Target, ChevronUp, Share2, X, Instagram, MessageCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getVolumeComparisons, buildFunShareLine } from "./volumeComparisons";
import { buildShareImage } from "./buildShareImage";
import { buildShareVideoBlob } from "./buildShareVideo";

// Simple inline TikTok glyph (lucide doesn't ship one)
const TikTokIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.321 5.562a5.122 5.122 0 01-.443-.258 6.228 6.228 0 01-1.137-.966c-.849-.966-1.166-1.947-1.282-2.633h.004c-.097-.57-.057-.94-.05-.94h-3.847v14.868c0 .199 0 .396-.008.591 0 .024-.002.046-.004.072 0 .01 0 .021-.003.032v.008a3.262 3.262 0 01-1.642 2.588 3.204 3.204 0 01-1.584.42c-1.796 0-3.252-1.465-3.252-3.274 0-1.81 1.456-3.274 3.252-3.274.34 0 .68.053 1.005.157l.005-3.918a7.178 7.178 0 00-1.01-.072C3.934 8.963.7 12.22.7 16.187c0 2.435 1.193 4.59 3.024 5.915a7.088 7.088 0 004.184 1.375c3.94 0 7.173-3.257 7.173-7.294V8.75a9.917 9.917 0 004.268 1.055V5.958a5.837 5.837 0 01-.028-.396z"/>
  </svg>
);

const confettiColors = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

const Confetti = () => {
  const [particles] = useState(() =>
    Array.from({ length: 40 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.5,
      color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
      size: 4 + Math.random() * 6,
      drift: -30 + Math.random() * 60,
    }))
  );

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          initial={{ y: -20, x: `${p.x}%`, opacity: 1, rotate: 0 }}
          animate={{ y: "110vh", x: `${p.x + p.drift}%`, opacity: 0, rotate: 360 + Math.random() * 360 }}
          transition={{ duration: 2.5 + Math.random(), delay: p.delay, ease: "easeIn" }}
          style={{ position: "absolute", width: p.size, height: p.size, backgroundColor: p.color, borderRadius: Math.random() > 0.5 ? "50%" : "2px" }}
        />
      ))}
    </div>
  );
};

const WorkoutCompletionCelebration = ({ data, workoutTitle, onClose }) => {
  const { user } = useAuth();
  const athleteName = user?.name || "";
  const { summary = {}, streak = {}, progression = [] } = data || {};
  const [showShare, setShowShare] = useState(false);
  const [generating, setGenerating] = useState(false);
  const hasPRs = summary.personal_records?.length > 0;

  const volume = summary.total_volume || 0;
  // Memoize so we don't re-roll the random picks on every re-render
  const funComparisons = useMemo(() => getVolumeComparisons(volume), [volume]);

  // Format volume nicely
  const formatVolume = (v) => {
    if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
    return v.toString();
  };

  const handleShare = async () => {
    if (generating) return;
    setGenerating(true);

    const sharePayload = {
      athleteName,
      workoutTitle: workoutTitle || "",
      volume,
      sets: summary.total_sets || 0,
      duration: summary.duration_minutes || 0,
      pr: hasPRs ? summary.personal_records[0] : null,
      funLine: buildFunShareLine(volume),
    };

    try {
      // Prefer an animated video (Instagram Stories / TikTok auto-play animated content)
      let file = null;
      try {
        const videoResult = await buildShareVideoBlob(sharePayload);
        if (videoResult && videoResult.blob) {
          file = new File(
            [videoResult.blob],
            `paladin-pr.${videoResult.extension}`,
            { type: videoResult.mime }
          );
        }
      } catch {
        // fall through to static image
      }

      // Fall back to static image if video generation isn't available
      if (!file) {
        const imgBlob = await buildShareImage(sharePayload);
        if (imgBlob) {
          file = new File([imgBlob], "paladin-pr.png", { type: "image/png" });
        }
      }

      if (!file) {
        setGenerating(false);
        return;
      }

      // Native share sheet — Instagram/TikTok/Messages/etc
      if (navigator.canShare && navigator.share && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: hasPRs ? "New PR on Paladin" : "Workout Complete — Paladin",
          });
          setGenerating(false);
          return;
        } catch {
          // User cancelled the share sheet — still fall through to download so they get the file
        }
      }

      // Fallback: trigger a download so they can upload manually
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setShowShare(true);
      setTimeout(() => setShowShare(false), 2500);
    } catch {
      // Silent failure — don't disrupt the celebration
    } finally {
      setGenerating(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        data-testid="workout-celebration"
      >
        {hasPRs && <Confetti />}

        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 40 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ type: "spring", damping: 20, stiffness: 200 }}
          className="relative w-full max-w-md z-20"
        >
          {/* Close button */}
          <button onClick={onClose} className="absolute -top-2 -right-2 bg-[#1E293B] p-2 rounded-full z-30 hover:bg-[#2D3B4F] transition-colors" data-testid="close-celebration">
            <X className="w-4 h-4 text-gray-400" />
          </button>

          {/* ─── SUMMARY CARD (ticket-style) ─── */}
          <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl overflow-hidden border border-[#334155]">
            {/* Card header */}
            <div className="relative bg-gradient-to-r from-primary/30 to-emerald-500/30 px-6 py-5 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring" }}
              >
                <Trophy className="w-10 h-10 text-amber-400 mx-auto mb-2" />
              </motion.div>
              <h2 className="text-xl font-bold text-white">Workout Complete!</h2>
              <p className="text-sm text-gray-300 mt-0.5">{workoutTitle}</p>
            </div>

            {/* Tear line */}
            <div className="relative h-4 flex items-center">
              <div className="absolute left-0 w-4 h-4 bg-black/90 rounded-full -translate-x-1/2" />
              <div className="absolute right-0 w-4 h-4 bg-black/90 rounded-full translate-x-1/2" />
              <div className="w-full border-t border-dashed border-[#334155]" />
            </div>

            {/* Stats grid */}
            <div className="px-6 py-4 grid grid-cols-2 gap-3">
              {[
                { icon: Clock, label: "Duration", value: `${summary.duration_minutes || 0}m`, color: "text-blue-400" },
                { icon: Dumbbell, label: "Exercises", value: summary.exercises_completed || 0, color: "text-emerald-400" },
                { icon: Target, label: "Total Sets", value: summary.total_sets || 0, color: "text-amber-400" },
                { icon: TrendingUp, label: "Volume", value: `${formatVolume(summary.total_volume || 0)} lbs`, color: "text-purple-400" },
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  className="bg-black/30 rounded-xl p-3 text-center"
                >
                  <stat.icon className={`w-4 h-4 ${stat.color} mx-auto mb-1`} />
                  <p className="text-lg font-bold text-white">{stat.value}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">{stat.label}</p>
                </motion.div>
              ))}
            </div>

            {/* ─── FUN VOLUME COMPARISONS ─── */}
            {funComparisons.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.75 }}
                className="mx-6 mb-4 bg-gradient-to-br from-amber-500/10 to-pink-500/10 border border-amber-500/20 rounded-xl p-4"
                data-testid="fun-comparisons"
              >
                <p className="text-[10px] font-bold text-amber-400 text-center uppercase tracking-widest mb-2">
                  That's as heavy as
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {funComparisons.map((comp, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.85 + i * 0.1, type: "spring", stiffness: 180 }}
                      className="bg-black/30 rounded-lg p-2 text-center"
                      data-testid={`fun-comparison-${i}`}
                    >
                      <p className="text-2xl leading-none mb-1">{comp.emoji}</p>
                      <p className="text-sm font-extrabold text-white leading-tight">{comp.qty}</p>
                      <p className="text-[9px] text-gray-400 leading-tight mt-0.5">{comp.label}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* PRs */}
            {hasPRs && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="mx-6 mb-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3"
              >
                <p className="text-amber-400 font-bold text-sm text-center mb-1">New Personal Records!</p>
                {summary.personal_records.map((pr, i) => (
                  <p key={i} className="text-xs text-amber-300/80 text-center">
                    {pr.exercise}: {pr.weight} lbs {pr.previous > 0 ? `(+${pr.weight - pr.previous})` : ""}
                  </p>
                ))}
              </motion.div>
            )}

            {/* ─── STREAK ─── */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="mx-6 mb-4 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-4 flex items-center gap-4"
              data-testid="streak-section"
            >
              <div className="relative flex-shrink-0">
                <motion.div
                  animate={{ scale: [1, 1.15, 1] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  <Flame className="w-10 h-10 text-orange-400" />
                </motion.div>
              </div>
              <div className="flex-1">
                <p className="text-2xl font-black text-white">{streak.current_streak || 1}-Day Streak</p>
                <p className="text-xs text-gray-400">{streak.this_week || 1} workout{(streak.this_week || 1) !== 1 ? "s" : ""} this week | {streak.total_workouts || 1} total</p>
              </div>
            </motion.div>

            {/* ─── LEVEL-UP PROGRESS BARS ─── */}
            {progression.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
                className="mx-6 mb-4 space-y-2"
                data-testid="progression-section"
              >
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  <ChevronUp className="w-3 h-3" /> Level Progress
                </p>
                {progression.map((p, i) => (
                  <div key={i} className="bg-black/30 rounded-lg p-2.5">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-xs font-medium text-white truncate max-w-[60%]">{p.exercise_name}</span>
                      <span className="text-[10px] text-primary font-semibold">{p.current_level}</span>
                    </div>
                    <div className="w-full bg-[#1E293B] rounded-full h-2 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${p.progress_percent}%` }}
                        transition={{ delay: 1.2 + i * 0.15, duration: 0.8, ease: "easeOut" }}
                        className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-400"
                      />
                    </div>
                    {p.next_level && (
                      <p className="text-[9px] text-gray-500 mt-1">Next: {p.next_level} ({p.current_index + 1}/{p.total_levels})</p>
                    )}
                  </div>
                ))}
              </motion.div>
            )}

            {/* Share / Close buttons */}
            <div className="px-6 pb-5 space-y-2">
              <div className="flex gap-3">
                <button
                  onClick={handleShare}
                  disabled={generating}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#1E293B] hover:bg-[#2D3B4F] disabled:opacity-70 disabled:cursor-wait text-white py-3 rounded-xl text-sm font-medium transition-colors"
                  data-testid="share-workout"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 h-4" />
                      {showShare ? "Saved!" : "Share PR"}
                    </>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 bg-primary hover:bg-primary/90 text-white py-3 rounded-xl text-sm font-bold transition-colors"
                  data-testid="done-celebration"
                >
                  Done
                </button>
              </div>

              {/* Platform affordance hint */}
              <button
                onClick={handleShare}
                disabled={generating}
                className="w-full flex items-center justify-center gap-2.5 text-[10px] text-gray-500 hover:text-gray-300 disabled:opacity-60 transition-colors pt-1"
                data-testid="share-platform-hints"
                aria-label="Share to social media"
              >
                <span className="uppercase tracking-widest">Share to</span>
                <Instagram className="w-3.5 h-3.5 text-pink-400/80" />
                <TikTokIcon className="w-3 h-3 text-white/80" />
                <MessageCircle className="w-3.5 h-3.5 text-emerald-400/80" />
                <span className="text-gray-600">& more</span>
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WorkoutCompletionCelebration;
